import { useCallback, useEffect, useMemo, useState } from 'react'
import * as fcl from '@blocto/fcl'
import { GET_ALL_NFTS_BY_ACCOUNT } from '../flow/scripts/getAllNFTsByAccount'
import {
  GET_LEGACY_LISTINGS_DETAILS_BY_ACCOUNT,
  GET_LISTINGS_DETAILS_BY_ACCOUNT,
  GET_STUCK_LISTINGS_DETAILS_BY_ACCOUNT
} from 'flow/scripts/getListingsDetailsByAccount'
import { GET_NFTS_IN_CHUNKS } from '../flow/scripts/getNFTsInChunks'
import { Moment, NFT, Pack, Reward } from 'types'
import { useWallets } from 'providers/WalletsProvider'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
export enum NFTsStatus {
  LOADING,
  DONE,
  ERROR
}
import { getDuplicateIds } from 'data/duplicates'
import { generateHash } from 'utils'
import { GET_TEMPLATE_BY_ID } from 'flow/scripts/getTemplateById'
import {
  getCrossmintMyPacks,
  getCrossmintMyMoments
} from 'services/crossmint.service'
import { getPackTemplateFromBackend } from 'services/api.service'
import { getEvmListingsByUser } from 'services/crossmint.marketplace.service'

const cacheData: { [key: string]: any } = {}

// Lets code outside this hook (marketplace list/cancel/buy, and eventually
// predictions staking) invalidate a wallet's cached moments without needing
// to render a live useUsersNFTs() instance themselves. Without this, an
// action that changes what a wallet holds (listing transfers a moment to
// escrow, staking transfers it to the predictions contract, etc.) leaves
// the cache serving pre-action data until something else happens to clear
// it — refetch()'s blanket-clear, or previously, an incidental remount
// (removed by adding the cache in the first place). Call with no argument
// to clear everything; with an address to clear just that wallet's entry.
export function invalidateUsersNFTsCache(walletAddress?: string): void {
  if (!walletAddress) {
    Object.keys(cacheData).forEach((k) => delete cacheData[k])
    return
  }
  delete cacheData[`crossmint:${walletAddress}`]
}

export default function useUsersNFTs(walletAddress?: string) {
  const featureFlags = useFeatureFlags()
  const [usersNFTs, setUsersNFTs] = useState<NFT<Moment | Pack | Reward>[]>([])
  const [duplicateIdList, setDuplicateIdList] = useState<string[]>([])
  const [hasLegacyMarketplaceListings, setHasLegacyMarketplaceListings] =
    useState<boolean>(false)
  const [totalLegacyMarketplaceListings, setTotalLegacyMarketplaceListings] =
    useState<number | undefined>()

  const [status, setStatus] = useState<NFTsStatus>(NFTsStatus.LOADING)
  // Bumped by refetch() to force the loadData effect to re-run and to bypass
  // the module-level cacheData on the next pass.
  const [refetchToken, setRefetchToken] = useState(0)
  const {
    flowUser,
    isConnected,
    usingCrossmint,
    crossmintWalletAddress,
    metamaskWalletAddress
  } = useWallets()
  // The active EVM wallet address for owned-NFTs lookups. Falls back across
  // Crossmint → MetaMask so the same code path lights up when MetaMask is
  // wired in a later sprint.
  const activeEvmAddress = crossmintWalletAddress || metamaskWalletAddress || ''

  const refetch = useCallback(() => {
    // Clear the in-memory cache so the next loadData() actually hits the
    // network rather than returning the pre-purchase result.
    Object.keys(cacheData).forEach((k) => delete cacheData[k])
    setRefetchToken((t) => t + 1)
  }, [])

  async function getAllMetadata(
    ids: string[],
    listedIds: string[],
    prices?: Record<string, string>
  ) {
    const cacheKey = await generateHash(ids.join(','))
    if (cacheData[cacheKey]) {
      setUsersNFTs(cacheData[cacheKey])
      setStatus(NFTsStatus.DONE)
      return
    }

    const hideIds: string[] =
      import.meta.env.VITE_HIDE_NFT_IDS?.split(',') ?? []

    const _ids = ids.filter((id) => !hideIds.includes(id as string)) as string[]

    try {
      const iNFTs: NFT<Moment | Pack | Reward>[] = []
      const delay = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms))
      const delayBetweenBatches = 1000 // Delay in milliseconds

      for (let i = 0; i < _ids.length; i += 1000) {
        const smallArray = _ids.slice(i, i + 1000)
        const result = await fcl.query({
          cadence: GET_NFTS_IN_CHUNKS,
          args: (arg: any, t: any) => [arg(smallArray, t.Array(t.UInt64))]
        })

        iNFTs.push(...(Object.values(result) as NFT<Moment | Pack | Reward>[]))

        // If there are more batches to process, wait for some time before the next one
        if (i + 1000 < _ids.length) {
          await delay(delayBetweenBatches)
        }
      }
      // add forSale property and price to NFTs in the Sale collection
      const _iNFTs = iNFTs.map((nft) => {
        nft.forSale = listedIds.includes(nft.id)
        if (nft.forSale)
          nft.salePrice = parseFloat(prices?.[nft.id] ?? '0').toFixed(2)
        return nft
      })
      setUsersNFTs(_iNFTs)
      cacheData[cacheKey] = iNFTs
      setStatus(NFTsStatus.DONE)
    } catch (e) {
      // add papertrail(e)
      console.error(e)
      setStatus(NFTsStatus.ERROR)
    }
  }

  useEffect(() => {
    if (!isConnected) {
      setUsersNFTs([])
      return
    }
    const loadData = async () => {
      setStatus(NFTsStatus.LOADING)
      setUsersNFTs([]) // Clear previous NFTs
      setDuplicateIdList([]) // Clear previous duplicates

      // ── Crossmint / EVM branch ───────────────────────────────────────────
      // Crossmint mints on Base (EVM); the FCL Flow query below would never
      // see those packs because flowUser is not populated for Crossmint users
      // and the pack address isn't a Flow address. Fetch the user's owned
      // packs from our backend instead, then enrich each one with the pack
      // template metadata (still sourced from Flow, since the templates were
      // not migrated — the on-chain mint moved, the template catalog did not).
      if (usingCrossmint) {
        // getCrossmintMyMoments() can be slow for high-volume wallets — the
        // backend batches on-chain reads sequentially, 10 at a time, so a
        // 10,000-moment wallet means ~1,000 sequential batches server-side.
        // This hook is called independently from 19+ pages with no shared
        // state between them, so without a cache, simply navigating between
        // pages re-runs the full fetch every time. Reuses the same
        // module-level cacheData the Flow branch already uses below —
        // refetch()'s existing blanket-clear (a few lines up) invalidates
        // this too, for free.
        const crossmintCacheKey = `crossmint:${activeEvmAddress}`
        if (activeEvmAddress && cacheData[crossmintCacheKey]) {
          setUsersNFTs(cacheData[crossmintCacheKey])
          setStatus(NFTsStatus.DONE)
          return
        }
        try {
          // Parallel: fetch Crossmint-custodial packs (from combinations),
          // BE-enriched moments (/api/crossmint/my-moments — Alchemy ownership
          // + Mongo metadata join), and active marketplace listings.
          // The on-chain getWalletOwnedNFTs path has been removed: it returned
          // Alchemy token IDs but sourced metadata from Flow templates, causing
          // mismatches vs the DB-based marketplace. /api/crossmint/my-moments
          // is the authoritative metadata source for both surfaces.
          const [packsResult, momentsResult, listingsResult] =
            await Promise.all([
              getCrossmintMyPacks().catch((e) => {
                console.error('[useUsersNFTs] getCrossmintMyPacks failed', e)
                return {
                  packs: [] as Awaited<
                    ReturnType<typeof getCrossmintMyPacks>
                  >['packs']
                }
              }),
              // Returns null on any error — service already swallows + warns.
              getCrossmintMyMoments(),
              // Fetch active marketplace listings — items transferred to the
              // escrow contract no longer appear in getCrossmintMyMoments, so
              // we fetch them separately and add them as forSale:true moments
              // so the Collectibles tab badge reflects the true total.
              activeEvmAddress
                ? getEvmListingsByUser(activeEvmAddress).catch((e) => {
                    console.error(
                      '[useUsersNFTs] getEvmListingsByUser failed',
                      e
                    )
                    return { wallet: '', listings: [] as any[] }
                  })
                : Promise.resolve({ wallet: '', listings: [] as any[] })
            ])

          const backendPacks = packsResult.packs
          const backendMoments = momentsResult?.moments ?? []
          const activeListings = listingsResult.listings ?? []

          if (
            backendPacks.length === 0 &&
            backendMoments.length === 0 &&
            activeListings.length === 0
          ) {
            if (activeEvmAddress) cacheData[crossmintCacheKey] = []
            setUsersNFTs([])
            setStatus(NFTsStatus.DONE)
            return
          }

          // Track pack template IDs and listing template IDs separately.
          // The pack endpoint (/templates/pack/:id) only serves pack templates;
          // calling it for listing template IDs (moments, upgrade/reward tokens)
          // always 404s. Upgrade/reward tokens (templateId >= 90,000) are
          // definitively not packs and must never hit that endpoint.
          const packTemplateIds = new Set<string>()
          backendPacks.forEach((p) => packTemplateIds.add(p.packTemplateId))

          const listingTemplateIds = new Set<string>()
          activeListings.forEach((l: any) => {
            if (l.templateId) listingTemplateIds.add(String(l.templateId))
          })

          const allTemplateIds = new Set([
            ...packTemplateIds,
            ...listingTemplateIds
          ])

          const templateById: Record<string, any> = {}
          await Promise.all(
            Array.from(allTemplateIds).map(async (tid) => {
              try {
                // Only call the pack endpoint for actual pack template IDs.
                // Listing template IDs (moments, upgrade/reward tokens) always
                // 404 the pack endpoint; skip them to avoid spurious errors.
                if (packTemplateIds.has(tid)) {
                  // ── Pack templates: BE first ───────────────────────────────
                  // Crossmint/Base packs (e.g. templateId 1013) aren't on
                  // Flow, so the Cadence query below returns nothing. The BE
                  // serves the same Flow-shaped JSON for known pack templates.
                  const beTpl = await getPackTemplateFromBackend(tid)
                  if (beTpl) {
                    templateById[tid] = beTpl
                    return
                  }
                }
                // ── Fallback: Flow Cadence for legacy/historical templates ─
                const tpl = await fcl.query({
                  cadence: GET_TEMPLATE_BY_ID,
                  args: (arg: any, t: any) => [arg(String(tid), t.UInt64)]
                })
                if (tpl) templateById[tid] = tpl
              } catch (e) {
                console.error('[useUsersNFTs] Failed to fetch template', tid, e)
              }
            })
          )

          // Synthesize Pack NFTs from Crossmint combinations.
          //
          // Filter out anything that's already been opened (status OPENED or
          // OPENED_ON_CHAIN) — the Trophy Cabinet Packs grid should only show
          // actionable packs. The minted moments will appear in the
          // Collectibles tab via /api/crossmint/my-moments instead. Anything
          // without a status field is treated as actionable (backwards-compat
          // for BE responses that haven't been deployed yet).
          //
          // Force immutableData.type='pack' regardless of what the BE returns —
          // the /templates/pack/:id endpoint serves pack templates exclusively,
          // but the underlying Mongo doc carries the original Flow-era tier
          // value (e.g. "standard"/"deluxe") in `type`. Downstream useMemo
          // filters categorise NFTs by this field; without the override every
          // Crossmint pack would be silently dropped from the Packs tab.
          const synthesizedPacks: NFT<Pack>[] = backendPacks
            .filter(
              (p) => p.status !== 'OPENED' && p.status !== 'OPENED_ON_CHAIN'
            )
            .map((p) => {
              const tpl = templateById[p.packTemplateId]
              if (!tpl) return null
              const tplWithType = {
                ...tpl,
                immutableData: {
                  ...(tpl.immutableData ?? {}),
                  type: 'pack'
                }
              }
              return {
                id: p.id,
                mintNumber: '',
                maxSupply: tpl.maxSupply ?? '',
                templateData: tplWithType,
                floorPrice: 0,
                forSale: false,
                isGenesisBall: false,
                status: p.status
              } as NFT<Pack>
            })
            .filter((n): n is NFT<Pack> => n !== null)

          // BE-enriched moments from /api/crossmint/my-moments are the sole
          // source for moment/reward NFTs — Alchemy ownership + Mongo metadata
          // joined server-side. Force immutableData.type='moment' as a fallback
          // because the Trophy Cabinet filter requires type === 'moment' or
          // 'club-badge' and the BE response may omit the field.
          const backendIds = new Set(
            (backendMoments as unknown as NFT<Moment>[]).map((m) => m.id)
          )
          const normalizedBackendMoments = (
            backendMoments as unknown as NFT<Moment>[]
          ).map((m) => ({
            ...m,
            templateData: {
              ...m.templateData,
              immutableData: {
                ...(m.templateData?.immutableData ?? {}),
                type: m.templateData?.immutableData?.type ?? 'moment'
              }
            }
          }))

          // Synthesize NFTs for items currently listed on the marketplace.
          // These are transferred to the escrow contract so they don't appear
          // in getCrossmintMyMoments. We add them with forSale:true so the
          // Collectibles tab badge = not-for-sale + for-sale.
          // EvmListing.metadata already carries headline/thumbnail/tier/player
          // so we use it directly and fall back to the fetched template doc.
          const synthesizedListings: NFT<Moment>[] = activeListings
            .filter((l: any) => !backendIds.has(String(l.tokenId)))
            .map((l: any) => {
              // Prefer the template's real immutableData, which the listings
              // endpoint now returns in full. Rebuilding it field by field
              // dropped everything not in the list below — video, description,
              // badges, match details, jumper number — so a moment looked
              // stripped down the moment it was listed.
              const tpl = templateById[String(l.templateId)]
              const immutable = l.immutableData ?? tpl?.immutableData ?? {}
              const serial = l.serial != null ? String(l.serial) : ''
              return {
                id: String(l.tokenId),
                mintNumber: serial,
                maxSupply: l.maxSupply ?? tpl?.maxSupply ?? '',
                issuedSupply: l.issuedSupply ?? tpl?.issuedSupply ?? '',
                templateData: {
                  templateId: String(l.templateId),
                  serial: l.serial ?? 0,
                  issuedSupply: String(
                    l.issuedSupply ?? tpl?.issuedSupply ?? ''
                  ),
                  maxSupply: String(l.maxSupply ?? tpl?.maxSupply ?? ''),
                  immutableData: {
                    ...immutable,
                    type: immutable.type ?? 'moment',
                    headline:
                      immutable.headline ??
                      l.metadata?.headline ??
                      l.metadata?.name ??
                      '',
                    thumbnail:
                      immutable.thumbnail ??
                      l.metadata?.thumbnail ??
                      l.metadata?.image ??
                      '',
                    tier: immutable.tier ?? l.metadata?.tier ?? '',
                    player: immutable.player ?? l.metadata?.player ?? '',
                    collectionName:
                      immutable.collectionName ??
                      l.metadata?.collectionName ??
                      '',
                    date: immutable.date ?? '',
                    maxSerial: immutable.maxSerial ?? '',
                    // This token's own serial, not the template's.
                    serial
                  }
                },
                floorPrice: 0,
                forSale: true,
                salePrice: l.priceUsd ?? '0',
                isGenesisBall: false
              } as NFT<Moment>
            })

          const all = [
            ...synthesizedPacks,
            ...synthesizedListings,
            ...normalizedBackendMoments
          ] as NFT<Moment | Pack | Reward>[]

          if (activeEvmAddress) cacheData[crossmintCacheKey] = all
          setUsersNFTs(all)
          setStatus(NFTsStatus.DONE)
          return
        } catch (e) {
          console.error('[useUsersNFTs] Crossmint/EVM fetch failed:', e)
          setStatus(NFTsStatus.ERROR)
          return
        }
      }

      if (!walletAddress && !flowUser?.addr) {
        setStatus(NFTsStatus.DONE) // Or ERROR? Consider the desired state
        return
      }

      try {
        // Fetch duplicate IDs first
        const fetchedDuplicateIds = await getDuplicateIds()
        setDuplicateIdList(fetchedDuplicateIds)

        // Now fetch NFT IDs
        const unlistedMomentIds: string[] = await fcl.query({
          cadence: GET_ALL_NFTS_BY_ACCOUNT,
          args: (arg: any, t: any) => [
            arg(walletAddress || flowUser?.addr, t.Address)
          ]
        })

        let listedMomentPricesById: Record<string, string> = {}
        let listedMomentIds: string[] = []

        if (featureFlags.MARKETPLACE_ACTIVE === true) {
          listedMomentPricesById = await fcl.query({
            cadence: GET_LISTINGS_DETAILS_BY_ACCOUNT,
            args: (arg: any, t: any) => [
              arg(walletAddress || flowUser?.addr, t.Address)
            ]
          })
          listedMomentIds = Object.keys(listedMomentPricesById)

          // check for legacy marketplace listings in production only
          if (import.meta.env.VITE_ENVIRONMENT === 'production') {
            const legacyListedMomentPricesById = await fcl.query({
              cadence: GET_LEGACY_LISTINGS_DETAILS_BY_ACCOUNT,
              args: (arg: any, t: any) => [
                arg(walletAddress ?? flowUser.addr, t.Address)
              ]
            })

            const stuckListings = await fcl.query({
              cadence: GET_STUCK_LISTINGS_DETAILS_BY_ACCOUNT,
              args: (arg: any, t: any) => [
                arg(walletAddress ?? flowUser?.addr, t.Address)
              ]
            })

            setTotalLegacyMarketplaceListings(
              Object.keys(legacyListedMomentPricesById).length +
                Object.keys(stuckListings).length
            )

            setHasLegacyMarketplaceListings(
              Object.keys(legacyListedMomentPricesById).length > 0 ||
                Object.keys(stuckListings).length > 0
            )
          }
        }

        const allMomentsIds = [...unlistedMomentIds, ...listedMomentIds]

        // Fetch metadata only after getting all IDs
        // Note: getAllMetadata sets status to DONE/ERROR internally
        await getAllMetadata(
          allMomentsIds,
          listedMomentIds,
          listedMomentPricesById
        )
      } catch (e) {
        if (
          (e as { stack: string }).stack?.includes(
            'could not borrow receiver reference'
          )
        ) {
          console.error(
            'address not found or collection not setup when getting NFTs for account',
            walletAddress || flowUser?.addr
          )
          setStatus(NFTsStatus.DONE) // Set status even on soft error
        } else {
          console.error('Error fetching user NFTs or duplicates:', e)
          setStatus(NFTsStatus.ERROR)
        }
      }
    }

    if (isConnected) {
      loadData()
    } else {
      setUsersNFTs([])
      setDuplicateIdList([])
      setStatus(NFTsStatus.DONE) // Or LOADING? Consider initial state
    }
  }, [
    walletAddress,
    flowUser,
    isConnected,
    featureFlags.MARKETPLACE_ACTIVE,
    refetchToken,
    usingCrossmint,
    activeEvmAddress
  ])

  // Filter NFTs by type
  // userPacks
  const packs: NFT<Pack>[] = useMemo(() => {
    return usersNFTs.filter(
      (nft) => nft.templateData.immutableData.type === 'pack'
    ) as unknown as NFT<Pack>[]
  }, [usersNFTs])

  // userMoments
  const moments: NFT<Moment>[] = useMemo(() => {
    // Perform filtering and mapping inside the useMemo callback
    return (
      usersNFTs.filter(
        (nft) =>
          nft.templateData.immutableData.type === 'moment' ||
          nft.templateData.immutableData.type === 'club-badge'
      ) as unknown as NFT<Moment>[]
    ).map((moment) => {
      // fix metadata
      const thumb = moment.templateData.immutableData.thumbnail
      if (typeof thumb === 'string') {
        moment.templateData.immutableData.thumbnail = thumb.replaceAll(
          '%20',
          '%2520'
        )
      }
      // add duplicate flag using the state variable
      moment.isDuplicate = duplicateIdList.includes(moment.id) // Use state variable
      moment.isGenesisBall =
        moment?.templateData.immutableData?.ballColor !== undefined
      return moment
    })
  }, [usersNFTs, duplicateIdList]) // Added duplicateIdList dependency

  // userRewards
  const rewards: NFT<Reward>[] = useMemo(() => {
    return usersNFTs.filter(
      (nft) => nft.templateData.immutableData.type === 'reward'
    ) as unknown as NFT<Reward>[]
  }, [usersNFTs])

  return {
    status,
    hasLegacyMarketplaceListings,
    totalLegacyMarketplaceListings,
    packs,
    moments,
    rewards,
    usersNFTs,
    refetch
  }
}
